import { Chip, Group, iconSizes, Skeleton, Text, Tooltip } from '@common/ui'
import { IconExclamationCircle } from '@common/ui/icons'
import { com } from '@eidu/web'
import { useMemo } from 'react'
import { PermissionPolicyDTO } from '../../domain/permission/PermissionPolicyDTO'
import EqualityHashMap from '../../util/EqualityHashMap'
import PermissionPolicyId = com.eidu.entity.permission.PermissionPolicyId

type AppliedPoliciesListProps = {
  selectedPolicyIds: ReadonlyArray<PermissionPolicyId>
  selectedPolicies: ReadonlyArray<PermissionPolicyDTO>
  loading?: boolean
  onRemove: (policyId: PermissionPolicyId) => void
}

const AppliedPoliciesList = ({ selectedPolicyIds, selectedPolicies, loading, onRemove }: AppliedPoliciesListProps) => {
  const policiesById = useMemo(() => {
    const map = new EqualityHashMap<PermissionPolicyId, PermissionPolicyDTO>()
    selectedPolicies.forEach((p) => map.set(p.policyId, p))
    return map
  }, [selectedPolicies])

  if (selectedPolicyIds.length === 0) {
    return (
      <div style={{ paddingInline: 16, paddingBlock: 8 }}>
        <Text size="sm" c="dimmed">
          No policies applied.
        </Text>
      </div>
    )
  }

  return (
    <Group gap="xs" align="center" style={{ flexWrap: 'wrap', paddingInline: 16, paddingTop: 12 }}>
      {selectedPolicyIds.map((id) => {
        const policy = policiesById.get(id)
        if (policy) {
          return (
            <Chip key={id.toString()} size="sm" onRemove={() => onRemove(id)}>
              {policy.policy.name}
            </Chip>
          )
        }
        if (loading) {
          return <Skeleton key={id.toString()} radius="sm" width={140} height={24} />
        }
        return (
          <Tooltip key={id.toString()} label={`Unknown policy: ${id.toString()}`}>
            <Chip tone="caution" size="sm" onRemove={() => onRemove(id)}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <IconExclamationCircle size={iconSizes.xs} stroke={1.5} />
                Unknown policy
              </span>
            </Chip>
          </Tooltip>
        )
      })}
    </Group>
  )
}

export default AppliedPoliciesList
